import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NavigationService } from '../../shared/services/navigation.service';
import { DataTableColumn } from '../../shared/data-table/data-table.component';
import { TranslateService } from '@ngx-translate/core';
import { BlueyonderUserService } from '../shared/blueyonder-user/blueyonder-user.service';
import { ModalService } from 'app/shared/services/modal.service';
import { CarrierService } from '../shared/carrier.service';
import { Carrier } from '../shared/carrier.model';
import { BlueyonderUserSearchCriteriaModel } from '../shared/blueyonder-user/blueyonder-user-search-criteria.model';
import { LoaderService } from '../../shared/services/loader.service';
import { AuthenticationService } from 'app/shared/services/authentication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { finalize } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { TmsType } from '../shared/tms-type.model';
import { BlueyonderUser } from '../shared/blueyonder-user/blueyonder-user.model';

@Component({
  selector: 'app-blueyonder-user-list',
  templateUrl: './blueyonder-user-list.component.html',
  styleUrls: ['./blueyonder-user-list.component.scss']
})
export class BlueyonderUserListComponent implements OnInit {
  columns: DataTableColumn[]                 = [];
  carriers: Carrier[]                        = [];
  carrier: Carrier;
  userCarrier: Carrier;
  filters: BlueyonderUserSearchCriteriaModel = {};
  canSeeAllCarriers                          = false;
  showCarrierSelectionHelper                 = true;

  loadingSubject: Subject<number> = new Subject<number>();

  @ViewChild('userCodeColumn', { static: true })
  userCodeColumn: TemplateRef<any>;

  @ViewChild('userNameColumn', { static: true })
  userNameColumn: TemplateRef<any>;

  @ViewChild('userGroupColumn', { static: true })
  userGroupColumn: TemplateRef<any>;

  @ViewChild('actionsColumn', { static: true })
  actionsColumn: TemplateRef<any>;

  @ViewChild('deleteModal', { static: true })
  deleteModal: TemplateRef<any>;

  @ViewChild('resetModal', { static: true })
  resetModal: TemplateRef<any>;

  loadPage = (page: number, pageSize: number) =>
    this.blueyonderUserService.search(page - 1, pageSize, this.filters, this.canSeeAllCarriers);

  constructor(
    private navigation: NavigationService,
    private blueyonderUserService: BlueyonderUserService,
    private translate: TranslateService,
    private modalService: ModalService,
    private carrierService: CarrierService,
    private loader: LoaderService,
    private authService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router,
    private errorHandler: ErrorHandlerService,
    private toastr: ToastrService,
  ) {
    this.userCarrier       = this.authService.getUserProfile().carrier;
    this.canSeeAllCarriers = this.authService.canManageBlueyonderUserAllCarriers();

    this.route.queryParams.subscribe(params => {
      if (this.canSeeAllCarriers) {
        this.filters.carrierId = params.carrierId;
      } else {
        this.updateQueryParams();
      }
    });
  }

  ngOnInit(): void {
    this.loadCarrierData();
    this.columns = [
      {
        name: 'userCode',
        displayed: true,
        translationKey: 'user-dashboard.blueyonder-user.list.table.userCode',
        template: this.userCodeColumn,
        sortable: false,
      },
      {
        name: 'userName',
        displayed: true,
        translationKey: 'user-dashboard.blueyonder-user.list.table.userName',
        template: this.userNameColumn,
        sortable: false,
      },
      {
        name: 'userGroup',
        displayed: true,
        translationKey: 'user-dashboard.blueyonder-user.list.table.userGroup',
        template: this.userGroupColumn,
        sortable: false,
      },
      {
        name: 'actions',
        displayed: true,
        translationKey: 'user-dashboard.blueyonder-user.list.table.actions.label',
        template: this.actionsColumn,
        sortable: false,
        alignRight: true,
        overflowVisible: true,
      },
    ];
  }

  loadCarrierData(): void {
    if (this.canSeeAllCarriers) {
      this.carrierService.getAllLightByTmsType(TmsType.BLUEYONDER)
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(carriers => {
          this.carriers = carriers;

          if (this.filters.carrierId && carriers) {
            const selectedCarrier = carriers.find(carrier => carrier.id === this.filters.carrierId);
            if (selectedCarrier) {
              this.carrier = selectedCarrier;
              this.selectedCarrierChanged(selectedCarrier);
            } else {
              this.filters.carrierId          = null;
              this.showCarrierSelectionHelper = true;
              this.updateQueryParams();
            }
          }
        });
    } else {
      this.filters.carrierId          = this.userCarrier.id;
      this.showCarrierSelectionHelper = false;
    }
  }

  selectedCarrierChanged(carrier: Carrier) {
    this.carrier                    = carrier;
    this.filters.carrierId          = carrier.id;
    this.showCarrierSelectionHelper = false;

    this.updateQueryParams();

    this.loadingSubject.next(1);
  }

  updateQueryParams() {
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: { carrierId: this.filters.carrierId }
      }
    );
  }

  goToCreateBlueyonderUser() {
    this.navigation.blueyonderUsers()
      .create(this.filters.carrierId)
      .go();
  }

  goToResetBlueyonderUser(user: BlueyonderUser) {
    this.filters.userCode = user.userCode;
    this.modalService.open({
      title: this.translate.instant('user-dashboard.blueyonder-user.modal.title.reset'),
      size: 'sm',
      data: {
        user: user
      },
      content: this.resetModal
    }).subscribe(() => {
      this.loader.startLoad();
      this.blueyonderUserService.resetBlueyonderUser(this.filters, this.canSeeAllCarriers)
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(() => {
          this.toastr.success(this.translate.instant(
            'user-dashboard.blueyonder-user.list.successes.reset',
            { userName: user.userName }
          ));
        }, error => this.errorHandler.handleHttpError(
          error.error.message,
          this.translate.instant('user-dashboard.blueyonder-user.list.error.reset', { userName: user.userName }),
          false
        ));
    });
  }

  goToDeleteBlueyonderUser(user: BlueyonderUser) {
    this.filters.userCode = user.userCode;
    this.modalService.open({
      title: this.translate.instant('user-dashboard.blueyonder-user.modal.title.delete'),
      size: 'sm',
      data: {
        user: user
      },
      content: this.deleteModal
    }).subscribe(() => {
      this.loader.startLoad();
      this.blueyonderUserService.deleteBlueyonderUser(this.filters, this.canSeeAllCarriers)
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(() => {
          this.toastr.success(this.translate.instant(
            'user-dashboard.blueyonder-user.list.successes.delete',
            { userName: user.userName }
          ));
          this.loadingSubject.next()
        }, error => this.errorHandler.handleHttpError(
          error.error.message,
          this.translate.instant('user-dashboard.blueyonder-user.list.error.delete', { userName: user.userName }),
          false
        ));
    });
  }
}
